import { CopyOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import { App, Form, Input, Modal } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { DoctorSelect } from '../../../components/DoctorSelect';
import { hasFrontDeskRole } from '../../../utils/user.ts';
import type { Doctor, Profile } from '../Profiles.tsx';
import { CREATE_PROFILE_MUTATION } from './CopyProfileModal.gql';

export const CopyProfileModal: React.FC<{
  doctors: Doctor[];
  currentDoctorId: string;
  profile: Profile | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ doctors, currentDoctorId, profile, onClose, onSuccess }) => {
  const [createProfileMutation, { loading }] = useMutation(CREATE_PROFILE_MUTATION);
  const [name, setName] = useState<string>(profile?.name ?? '');
  const [doctorId, setDoctorId] = useState<string>(currentDoctorId);
  const { message } = App.useApp();
  const auth = useAuth();

  const copy = async () => {
    if (!name.trim().length) {
      message.warning('Bitte geben Sie eine Bezeichnung an');
      return;
    }

    try {
      await createProfileMutation({
        variables: {
          input: {
            doctorId: doctorId,
            name: name,
            hotKey: null,
            parameters:
              profile?.parameters.map(it => ({
                parameterId: it.parameter.id,
                preferredBillingType: it.preferredBillingType,
              })) ?? [],
            diagnose: profile?.diagnose ?? '',
            diagnoseIds: profile?.diagnoses.map(it => it.id) ?? [],
          },
        },
      });

      message.success('Profil wurde kopiert');
      onSuccess();
    } catch {
      message.error('Profil konnte nicht kopiert werden');
    }
  };

  return (
    <Modal
      width={500}
      title={`Profil ${profile?.name} kopieren`}
      okText="Kopieren"
      okButtonProps={{ icon: <CopyOutlined /> }}
      open={!!profile}
      onCancel={onClose}
      onOk={copy}
      confirmLoading={loading}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item label="Neue Bezeichnung" required>
          <Input value={name} onChange={e => setName(e.target.value)} />
        </Form.Item>
        {doctors.length > 1 && !hasFrontDeskRole(auth.user) && (
          <Form.Item label="Kopieren zu Zuweiser">
            <DoctorSelect
              style={{ width: '100%' }}
              onChange={value => setDoctorId(value as string)}
              doctors={doctors}
              selected={doctorId}
              allowClear={false}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
